import { LibraryBooks, LibraryBooksOutlined } from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import GridViewIcon from '@mui/icons-material/GridView';
import GridViewSharpIcon from '@mui/icons-material/GridViewSharp';
import KeyIcon from '@mui/icons-material/Key';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import LaptopIcon from '@mui/icons-material/Laptop';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import TableViewIcon from '@mui/icons-material/TableView';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import { Button, CircularProgress, Container, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import LogoSvg from '../../../../assets/socotra-white-horiz.svg';
import {
	AuthData,
	ROLES_AND_PERMISSION_ENABLED,
	TABLES_AND_STATIC_DOCS_ENABLED,
} from '../../../../context/auth';
import UserContext from '../../../../context/userContext';
import { labelToID } from '../../../../util/util';
import { TenantResponse } from '../api/tenants';
import {
	ACCOUNT_BUTTON_LABEL,
	FEEDBACK_LINK,
	FEEDBACK_TEXT,
	INTEGRATIONS_TAB_TEXT,
	LOGOUT_ACTION_TEXT,
	LOGOUT_CONFIRM_ACTION_TEXT,
	LOGOUT_CONFIRM_INFO_TEXT,
	RESOURCE_GROUPS_TAB_TEXT,
	ROLES_TAB_TEXT,
	SCRIPT_LOGS_TAB_TEXT,
	STATIC_DOCS_TAB_TEXT,
	SWITCH_ROLES_BUTTON_LABEL,
	TABLES_TAB_TEXT,
	USERS_TAB_TEXT,
} from '../data/constants';
import HeaderUserDropdown from './HeaderUserDropdown';
const drawerWidth = 240;
const headerHeight = 64;

interface Props {
	authData: AuthData;
	currentTenant: TenantResponse;
	children?: React.ReactNode;
	error: boolean;
	loading: boolean;
}

interface LinkItemProps {
	label: string;
	icon: JSX.Element;
	iconSelected: JSX.Element;
	url: string;
	altUrls?: string[];
}

interface DropdownItem {
	label: string;
	icon?: JSX.Element;
	callback?: () => void;
}

//changes color of selected item to secondary color
const colorStyles = (isSelected: boolean) => {
	return {
		color: isSelected ? 'secondary.main' : 'common.white',
	};
};

function SidebarLinkItem(props: LinkItemProps) {
	const location = useLocation();
	const { label, icon, iconSelected, url, altUrls } = props;
	let isSelected = location.pathname.includes(url);
	altUrls?.forEach((path) => {
		if (location.pathname.includes(path)) {
			isSelected = true;
		}
	});
	const selectedIcon = isSelected ? iconSelected : icon;
	return (
		<ListItemButton
			id={`Sidebar__ListItem--${labelToID(label)}`}
			selected={isSelected}
			component={NavLink}
			to={url}
		>
			<ListItemIcon sx={colorStyles(isSelected)}>{selectedIcon}</ListItemIcon>
			<ListItemText sx={colorStyles(isSelected)} primary={label} />
		</ListItemButton>
	);
}

export default function Layout({
	authData,
	currentTenant,
	error,
	loading,
}: Props) {
	const { userRoles, changeRole } = React.useContext(UserContext);

	const [confirmLogout, setConfirmLogout] = React.useState(false);

	const hasMultipleRoles = userRoles.length > 1;

	const username = authData?.adminAuthData?.displayName || '';
	const rolesAndPermissionsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			ROLES_AND_PERMISSION_ENABLED
		];
	const tablesAndStaticDocsEnabled =
		authData?.tenantAuthorizationData?.configProperties?.[
			TABLES_AND_STATIC_DOCS_ENABLED
		];
	const linksArr: DropdownItem[] = [
		{
			label: currentTenant.hostname,
			icon: <LaptopIcon />,
			callback: null,
		},
		{
			label: ACCOUNT_BUTTON_LABEL(),
			icon: <AccountCircleOutlinedIcon />,
			callback: () => '/admin/account',
		},
	];

	const actionsArr: DropdownItem[] = [
		{
			label: LOGOUT_ACTION_TEXT(),
			icon: <ExitToAppIcon />,
			callback: () => setConfirmLogout(true),
		},
	];

	const userRolesArr: DropdownItem[] = hasMultipleRoles
		? [
				{
					label: SWITCH_ROLES_BUTTON_LABEL(),
					icon: <PersonOutlineIcon />,
					callback: changeRole,
				},
		  ]
		: [];

	const handleLogOut = () => {
		document.title = 'Socotra - Tenant v2';
		authData.logout();
	};

	return (
		<Box id='AdminPortal' height='100vh'>
			<CssBaseline />
			{/* Top navbar */}
			<Box
				id='AppBar'
				sx={{
					backgroundColor: 'background.default',
					color: 'common.black',
					display: 'flex',
					height: '64px',
					width: '100%',
				}}
			>
				<Box
					id='Sidebar__Button--Logo'
					component={NavLink}
					to='/admin/users'
					sx={{
						backgroundColor: 'primary.dark',
						height: '100%',
						flex: `0 0 ${drawerWidth}px`,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<LogoSvg />
				</Box>
				<Box flex={1} />
				<HeaderUserDropdown
					links={[...linksArr]}
					actions={[...userRolesArr, ...actionsArr]}
				>
					{username}
				</HeaderUserDropdown>
			</Box>
			<Box id='MainContent' sx={{ height: `calc(100vh - ${headerHeight}px)` }}>
				<Box display='flex' height='100%'>
					{/* Sidebar */}
					<Stack
						key='drawer-links'
						id='Sidebar'
						sx={{
							height: '100%',
							backgroundColor: 'primary.dark',
							color: 'white',
							boxSizing: 'border-box',
							flex: `0 0 ${drawerWidth}px`,
						}}
					>
						<List sx={{ flexGrow: 1 }}>
							<List id='Sidebar__List'>
								<SidebarLinkItem
									label={USERS_TAB_TEXT()}
									icon={<PeopleAltOutlinedIcon />}
									iconSelected={<PeopleAltIcon />}
									url='/admin/users'
								/>
								<SidebarLinkItem
									label={INTEGRATIONS_TAB_TEXT()}
									icon={<GridViewIcon />}
									iconSelected={<GridViewSharpIcon />}
									url='/admin/integrations/external-raters'
									altUrls={[
										'/admin/integrations/data-autofills',
										'/admin/integrations/webhooks',
									]}
								/>
								<SidebarLinkItem
									label={SCRIPT_LOGS_TAB_TEXT()}
									icon={<AssignmentOutlinedIcon />}
									iconSelected={<AssignmentIcon />}
									url='/admin/logs'
								/>
								{rolesAndPermissionsEnabled && (
									<SidebarLinkItem
										label={ROLES_TAB_TEXT()}
										icon={<KeyOutlinedIcon />}
										iconSelected={<KeyIcon />}
										url='/admin/roles'
									/>
								)}
								{tablesAndStaticDocsEnabled && (
									<>
										<SidebarLinkItem
											label={TABLES_TAB_TEXT()}
											icon={<TableViewOutlinedIcon />}
											iconSelected={<TableViewIcon />}
											url='/admin/tables'
										/>
										<SidebarLinkItem
											label={STATIC_DOCS_TAB_TEXT()}
											icon={<DescriptionOutlinedIcon />}
											iconSelected={<DescriptionIcon />}
											url='/admin/static-docs'
										/>
									</>
								)}
								<SidebarLinkItem
									label={RESOURCE_GROUPS_TAB_TEXT()}
									icon={<LibraryBooksOutlined />}
									iconSelected={<LibraryBooks />}
									url='/admin/resource-groups'
								/>
							</List>
						</List>
						<Stack
							p={2}
							spacing={2}
							direction='column'
							alignItems='center'
							justifyContent='center'
							sx={{ color: 'white.lightest' }}
						>
							<Typography
								target='_blank'
								href={FEEDBACK_LINK()}
								component='a'
								color='inherit'
								sx={{ textDecoration: 'none' }}
							>
								{FEEDBACK_TEXT()}
							</Typography>
						</Stack>
					</Stack>
					{/* Main content */}
					<Box
						component='main'
						sx={{
							backgroundColor: 'background.default',
							borderTop: '1px solid',
							borderColor: 'divider',
							flex: 1,
						}}
					>
						<Container
							maxWidth='lg'
							sx={{
								pt: 3,
								pb: 3,
								height: '100%',
							}}
						>
							{!error && !loading && <Outlet />}
							{!error && loading && (
								<Box
									width='100%'
									display='flex'
									alignItems='center'
									justifyContent='center'
								>
									<CircularProgress />
								</Box>
							)}
						</Container>
					</Box>
				</Box>
			</Box>
			{/* Dialogs */}
			<Dialog open={confirmLogout}>
				<DialogTitle>{LOGOUT_CONFIRM_ACTION_TEXT()}</DialogTitle>
				<DialogContent>
					<DialogContentText>{LOGOUT_CONFIRM_INFO_TEXT()}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmLogout(false)}>Cancel</Button>
					<Button color='secondary' onClick={handleLogOut}>
						{LOGOUT_ACTION_TEXT()}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
